import type {PayloadAction} from "@reduxjs/toolkit";
import {createSlice, current} from "@reduxjs/toolkit";
import {
    EkycData,
    FormData,
    FormStage,
    FormState,
    FormStep,
    FormTheme,
    LoanCalculate,
    StaticFetchData,
} from "@/lib/types/finsey/form";
import {Transform} from "@/lib/helper";
import {LoanService} from "@/services";

const formStage: FormStage = {
    introStage: true,
    currentStage: 0,
    isAcceptConsent: true,
    stage1: "",
    stage2: "",
    stage3: "",
    stage4: "",
    stage5: "",
};

const formStep: FormStep = {
    currentStep: 0,
    steps: [
        {
            title: 'Thông tin số tiền cần hỗ trợ',
            time: '1 phút',
            fields: ['month', 'coupon', 'schoolType', 'school', 'semester', 'admissionMethod', 'referral']
        },
        {
            title: 'Thông tin cá nhân và liên hệ',
            time: '3 phút',
            fields: ['name', 'student_id', 'email', 'additionalEmail', 'phoneNumber', 'studentAccount', 'studentPassword', 'cosignerName', 'cosignerRelationship', 'cosignerPhoneNumber', 'isBadDebt']
        },
        {
            title: 'Xác thực định danh',
            time: '5 phút',
            fields: []
        },
        {
            title: 'Trắc nghiệm tính cách',
            time: '10 phút',
            fields: []
        },
        {
            title: 'Xác nhận và gửi',
            time: '1 phút',
            fields: []
        },
    ]
}

const formData: FormData = {
    //Stage One
    amount: 1000000,
    month: 4,
    coupon: '',
    discount: 0,
    schoolType: "",
    school: "",
    semester: "",
    admissionMethod: "",
    referral: '',

    //Stage Two
    name: "",
    student_id: "",
    email: "",
    additionalEmail: "",
    phoneNumber: "",
    studentAccount: "",
    studentPassword: "",

    gpaFile: "",
    studentFile: "",

    cosignerName: "",
    cosignerRelationship: "",
    cosignerPhoneNumber: "",
    //otp status
    isOtpVerified: false,
    //cccd
    cccd: "",
    //is bad debt
    isBadDebt: ''
};

const loanCalculate: LoanCalculate = {
    totalPayPerMonth: 0,
    total: 0,
    baseTotal: 0,
    serviceFee: 0,
    interestTotal: 0,
    payStartDate: '',
    interestTotalWithoutDiscount: 0,
    discountAmount: 0,
};

const staticData: StaticFetchData = {
    isFetched: false,
    schools: [],
    rates: {},
    questions: [],
};

const ekycData: EkycData = {
    face: {
        image: "",
        status: "idle",
        message: "Chụp ảnh chân dung của bạn",
    },
    idFront: {
        image: "",
        status: "disabled",
        message: "Chụp ảnh mặt trước của CCCD",
    },
    idBack: {
        image: "",
        status: "disabled",
        message: "Chụp ảnh mặt sau của CCCD",
    },
    faceMatching: {
        status: 'disabled',
        message: '',
        idData: {}
    }
};

const formTheme: FormTheme = {
    school: ''
}

const initialState: FormState = {
    formStage: formStage,
    formStep: formStep,
    formData: formData,
    formTheme: formTheme,
    loanCalculate: loanCalculate,
    loanScheduleCalculate: [],
    staticFetchData: staticData,
    quizAnswers: [],
    ekycData: ekycData
};

export const formSlice = createSlice({
    name: "form",
    initialState,
    reducers: {
        initializeData: (state, actions: PayloadAction<any>) => {
            //init data
            let rates = actions.payload.rates;
            rates.interest_rate = rates.interest_rate.filter((item: any) => {
                return item.partner != 'upgrad' && item.type != 'reapply'
            })
            state.staticFetchData.rates = rates
            state.staticFetchData.schools = actions.payload.schools
            state.staticFetchData.questions = actions.payload.questions

            //init first quiz
            state.quizAnswers = [{
                index: actions.payload.questions[0].Question_Index,
                facet: actions.payload.questions[0].Facet,
                question: actions.payload.questions[0].Content_VN,
                answer: 0,
                isNew: true
            }]

            //mark fetched
            state.staticFetchData.isFetched = true
        },
        setLoanProductData: (state, actions: PayloadAction<any>) => {
            state.loanProductData = actions.payload.loanProductData
        },
        setSchoolList: (state, actions: PayloadAction<any>) => {
            state.schoolList = actions.payload.schoolList
        },
        setDraftData: (state, actions: PayloadAction<any>) => {
            state.draftData = actions.payload.draft
        },
        setSchoolTheme: (state, actions: PayloadAction<'' | 'umt'>) => {
            state.formTheme.school = actions.payload;
        },
        updateFormData: (state, actions: PayloadAction<any>) => {
            const transform = new Transform();
            let formData = actions.payload
            formData.amount = transform.transformMoneyToNumber(formData.amount)
            state.formData = {...current(state.formData), ...formData}
        },
        changeStep: (state, actions: PayloadAction<number>) => {
            state.formStep.currentStep = actions.payload
        },
        submitQuizAnswer: (state, actions: PayloadAction<number>) => {
            let answers = current(state.quizAnswers)
            let questions = current(state.staticFetchData.questions)
            if (answers.length < questions.length) {
                state.quizAnswers[0].answer = actions.payload
                state.quizAnswers.unshift({
                    index: questions[answers.length].Question_Index,
                    facet: questions[answers.length].Facet,
                    question: questions[answers.length].Content_VN,
                    answer: 0,
                    isNew: true
                })
            } else if (answers.length == questions.length) {
                state.quizAnswers[0].answer = actions.payload
            }
        },
        calculateLoanAmount: (
            state,
            actions: PayloadAction<{
                amount: any,
                month: any,
                discount?: any,
                overrideInterestRate?: number,
                overrideServiceRate?: number
            }>
        ) => {
            const loanService = new LoanService();
            const transform = new Transform();
            let amount = transform.transformMoneyToNumber(
                actions.payload.amount || "0"
            );

            let rates = {...current(state.staticFetchData.rates)}
            if (actions.payload.overrideInterestRate != undefined && actions.payload.overrideInterestRate >= 0) {
                rates.interest_rate = rates.interest_rate.map((item: any) => {
                    return {...item, Value: actions.payload.overrideInterestRate}
                });
                state.formData.discount = 0;
            }
            if (actions.payload.overrideServiceRate != undefined && actions.payload.overrideServiceRate >= 0) {
                rates.service_rate = {...rates.service_rate, Value: actions.payload.overrideServiceRate}
                state.formData.discount = 0;
            }
            if (actions.payload.discount && actions.payload.discount == -1) {
                state.formData.discount = 0;
            } else {
                state.formData.discount = actions.payload.discount ? actions.payload.discount : state.formData.discount ? state.formData.discount : 0;
            }

            const data = loanService.loanAmountIinfo(
                amount,
                actions.payload.month || 4,
                rates,
                state.formData.discount
            );

            const scheduleData = loanService.calculateSchedulePerMonth(
                amount,
                actions.payload.month || 4,
                rates,
                state.formData.discount
            );
            console.log({rates, data, scheduleData})
            state.loanCalculate = data;
            state.loanScheduleCalculate = scheduleData;
        },
        validateImageEkyc: (
            state,
            actions: PayloadAction<{
                file: any,
                status: 'idle' | 'disabled' | 'pass' | 'fail' | 'loading',
                message: string,
                type: 'face' | 'idFront' | 'idBack'
            }>
        ) => {
            //Update current type data
            let payload = actions.payload
            let ekycData = {
                image: payload.file,
                status: payload.status,
                message: payload.message
            }
            state.ekycData[payload.type] = ekycData

            //Enable next input
            if (payload.status == 'pass') {
                switch (payload.type) {
                    case 'face':
                        state.ekycData.idFront.status = 'idle'
                        break;
                    case 'idFront':
                        state.ekycData.idBack.status = 'idle'
                        break;
                    case 'idBack':
                        state.ekycData.faceMatching.status = 'idle'
                        break;
                    default:
                        break;
                }
            }
        },
        faceMatchingEkyc: (
            state,
            actions: PayloadAction<{
                status: 'idle' | 'disabled' | 'pass' | 'fail' | 'loading' | 'error',
                message: string,
                information?: any
            }>
        ) => {
            let payload = actions.payload
            state.ekycData.faceMatching = {
                status: payload.status,
                message: payload.message,
                idData: payload.information ?? {}
            }

            if (payload.status == 'fail' || payload.status == 'error') {
                state.ekycData = ekycData;
            } else {
                if (payload.information && payload.information.id) {
                    state.formData.cccd = payload.information.id
                }
            }
        },
        resetForm: (
            state,
            actions: PayloadAction
        ) => {
            state.formStage = formStage
            state.formStep = formStep
            state.formData = formData
            state.formTheme = formTheme
            state.loanCalculate = loanCalculate
            state.loanScheduleCalculate = []
            state.staticFetchData = staticData
            state.quizAnswers = []
            state.ekycData = ekycData
        }
    },
});

export const {
    initializeData,
    submitQuizAnswer,
    calculateLoanAmount,
    changeStep,
    updateFormData,
    validateImageEkyc,
    faceMatchingEkyc,
    setSchoolTheme,
    resetForm,
    setDraftData,
    setLoanProductData,
    setSchoolList
} =
    formSlice.actions;
export default formSlice;
